import {
  AlignLeft,
  Eye,
  FileText,
  ImageIcon,
  Headphones,
  Braces,
  Hash,
  Calendar,
  Box,
  MessageSquare,
  Quote,
  SquareStack,
  ToggleRight,
  type LucideIcon,
} from "lucide-react";
import type React from "react";
import { cn } from "@/lib/utils";
import {
  FLOW_SCALARS,
  type FlowTypeValue,
} from "@/components/SpotflowV2/flowTypes";
import { NoxusIcon } from "@/components/UI/NoxusIcon";

interface TypeMeta {
  label: string;
  icon: LucideIcon;
}

// Lucide's `variable` icon as a data URI, for surfaces that take an image
// src rather than a React component (node images, group icons).
export const GLOBAL_VARIABLE_ICON_DATA_URI =
  "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%234f46e5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M8 21s-4-3-4-9 4-9 4-9'/%3E%3Cpath d='M16 3s4 3 4 9-4 9-4 9'/%3E%3Cline x1='15' x2='9' y1='9' y2='15'/%3E%3Cline x1='9' x2='15' y1='9' y2='15'/%3E%3C/svg%3E";

const BUILTIN_ICONS: Record<FlowTypeValue, LucideIcon> = {
  str: AlignLeft,
  number: Hash,
  bool: ToggleRight,
  datetime: Calendar,
  File: FileText,
  object: Braces,
};

// Built-in types take their label from the registry; the rest are media and
// legacy types that only ever appear on outputs.
export const TYPE_META: Record<string, TypeMeta> = {
  ...Object.fromEntries(
    FLOW_SCALARS.map((t) => [
      t.value,
      { label: t.label, icon: BUILTIN_ICONS[t.value] },
    ])
  ),
  Image: { label: "Image", icon: ImageIcon },
  Audio: { label: "Audio", icon: Headphones },
  dict: { label: "Object", icon: Braces },
  basemodel: { label: "Model", icon: Box },
  Chat: { label: "Chat", icon: MessageSquare },
  Quote: { label: "Quote", icon: Quote },
};

const FALLBACK_META: TypeMeta = {
  label: "Unknown",
  icon: Braces,
};

// Human labels shown in type selectors (the registry's chip labels) →
// canonical `TYPE_META` key, so `DataTypeIcon`/`hasTypeIcon` resolve both a raw
// data-type ("str") and its display label ("Text").
const TYPE_LABEL_ALIASES: Record<string, string> = {
  ...Object.fromEntries(FLOW_SCALARS.map((t) => [t.label, t.value])),
  Date: "datetime",
  Objects: "object",
  // Variable `value_type`s (see `variableShape`) → canonical icon keys.
  // `number`/`datetime` already match a `TYPE_META` key directly.
  string: "str",
  boolean: "bool",
  file: "File",
  // "List of X" selectors strip to the plural element label ("Files"), which
  // isn't a TYPE_META key — map the plurals so they show the same icon.
  Files: "File",
  Images: "Image",
  Audios: "Audio",
};

const resolveTypeKey = (key: string): string => TYPE_LABEL_ALIASES[key] ?? key;

/** Whether a type label/data-type has a known icon (skips the fallback). */
export function hasTypeIcon(key: string): boolean {
  return resolveTypeKey(key) in TYPE_META;
}

interface DataTypeBadgeProps {
  dataType: string;
  isList?: boolean;
  label?: string;
  className?: string;
}

export function DataTypeBadge({
  dataType,
  isList,
  label,
  className,
}: DataTypeBadgeProps) {
  const meta = TYPE_META[resolveTypeKey(dataType)] ?? FALLBACK_META;
  const Icon = meta.icon;
  const baseLabel = label ?? meta.label;
  const displayLabel = isList ? `List of ${baseLabel}` : baseLabel;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-xs select-none text-muted-foreground",
        className
      )}
    >
      {isList ? (
        <SquareStack size={11} strokeWidth={2} className="shrink-0" />
      ) : (
        <Icon size={11} strokeWidth={2} className="shrink-0" />
      )}
      <span className="truncate min-w-0">{displayLabel}</span>
    </span>
  );
}

export function DataTypeIcon({
  dataType,
  size = 14,
  className,
  isList = false,
}: {
  dataType: string;
  size?: number;
  className?: string;
  isList?: boolean;
}) {
  const meta = TYPE_META[resolveTypeKey(dataType)] ?? FALLBACK_META;
  const Icon = meta.icon;
  // A list shows the element's own (scalar) icon hugged by brackets. The icon
  // renders exactly as in the scalar case so it keeps the same footprint, and
  // the brackets are absolute overlays at the edges so they add no width.
  if (isList) {
    return (
      <span
        className={cn(
          "relative inline-flex shrink-0 items-center justify-center text-muted-foreground",
          className
        )}
      >
        <span className="pointer-events-none absolute inset-y-0 left-0 w-[3px] border-y border-l border-muted-foreground" />
        {/* strokeWidth ÷ scale keeps the visual stroke matching the scalar icon */}
        <Icon size={size} strokeWidth={3} className="scale-[0.66]" />
        <span className="pointer-events-none absolute inset-y-0 right-0 w-[3px] border-y border-r border-muted-foreground" />
      </span>
    );
  }
  return (
    <Icon
      size={size}
      strokeWidth={2}
      className={cn("text-muted-foreground", className)}
    />
  );
}

interface DataChipProps {
  sourceImage?: string | null;
  sourceIconName?: string | null;
  sourceColor?: string;
  sourceLabel: string;
  dataType: string;
  variableLabel: string;
  isList?: boolean;
  size?: "default" | "sm";
  className?: string;
  sourceIcon?: LucideIcon;
  showSource?: boolean;
  labelClassName?: string;
  textClassName?: string;
  showVariableLabel?: boolean;
  showVariable?: boolean;
  bare?: boolean;
  // Amber ring: the ref resolves but won't work here (source not upstream,
  // type the field can't take).
  warning?: boolean;
  relaxedWidth?: boolean;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
}

export function DataChip({
  sourceImage,
  sourceIconName,
  sourceColor,
  sourceLabel,
  dataType,
  variableLabel,
  isList,
  size = "default",
  className,
  sourceIcon: SourceIcon,
  showSource = true,
  labelClassName,
  textClassName = "text-sm",
  showVariableLabel = true,
  showVariable = true,
  bare = false,
  warning = false,
  relaxedWidth = false,
  onMouseEnter,
  onMouseLeave,
}: DataChipProps) {
  const meta = TYPE_META[dataType] ?? FALLBACK_META;
  const TypeIcon = meta.icon;
  const sm = size === "sm";

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        "inline-flex items-center select-none max-w-full overflow-hidden",
        !bare && "rounded border border-border bg-background shadow-sm",
        warning &&
          !bare &&
          "border-warning-400 ring-1 ring-warning-400/50 shadow-none",
        className
      )}
    >
      {showSource && (
        <div
          className={cn(
            "flex items-center min-w-0 shrink",
            showVariable && "border-r border-border",
            sm ? "pl-1.5 pr-2 py-0.5 gap-1" : "pl-2 pr-2.5 py-1 gap-1.5"
          )}
        >
          {sourceIconName ? (
            <NoxusIcon icon={sourceIconName} size={sm ? "xsm" : "smd"} />
          ) : (
            <div
              className={cn(
                "flex items-center justify-center rounded-sm shrink-0 overflow-hidden",
                sm ? "size-4" : "size-5"
              )}
              style={{
                backgroundColor: sourceColor ?? (sm ? "#f9fafb" : "#f4f4f5"),
              }}
            >
              {SourceIcon ? (
                <SourceIcon
                  size={sm ? 9 : 11}
                  strokeWidth={2}
                  className="text-indigo-600"
                />
              ) : sourceImage ? (
                <img
                  src={sourceImage}
                  alt=""
                  className={cn("object-contain", sm ? "size-2.5" : "size-3.5")}
                />
              ) : (
                <div
                  className={cn(
                    "rounded-sm bg-white/30",
                    sm ? "size-2" : "size-2"
                  )}
                />
              )}
            </div>
          )}
          <span
            className={cn(
              textClassName,
              "text-foreground truncate min-w-0",
              !relaxedWidth && (sm ? "max-w-[90px]" : "max-w-[100px]")
            )}
          >
            {sourceLabel}
          </span>
        </div>
      )}
      {showVariable && (
        <div
          className={cn(
            "flex items-center min-w-0",
            sm ? "pl-1.5 pr-2 py-0.5 gap-1" : "pl-2 pr-2.5 py-1 gap-1.5",
            showSource ? "shrink-0" : "shrink"
          )}
        >
          <div
            className={cn(
              "flex items-center justify-center rounded-sm shrink-0 overflow-hidden",
              sm ? "size-4" : "size-5"
            )}
          >
            {isList ? (
              <SquareStack
                size={sm ? 10 : 14}
                strokeWidth={1.5}
                className="text-muted-foreground"
              />
            ) : (
              <TypeIcon
                size={sm ? 10 : 14}
                strokeWidth={1.5}
                className="text-muted-foreground"
              />
            )}
          </div>
          {showVariableLabel && (
            <span
              className={cn(
                textClassName,
                "truncate min-w-0",
                !relaxedWidth &&
                  (sm
                    ? showSource
                      ? "max-w-[80px]"
                      : "max-w-[180px]"
                    : showSource
                      ? "max-w-[200px]"
                      : "max-w-[260px]"),
                labelClassName ?? "text-muted-foreground"
              )}
            >
              {variableLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
